import request from './request';
import i18n from '@/i18n';
import util from '@/util';
import { AGREEMENT, CONTEXT } from '@/const';

const origin = process.env.NODE_ENV === 'production' ? location.origin : '';

const path = origin + `${CONTEXT.uniportal}/rest/hwidcenter`;

export default {
  /**
   * @typedef {Object} LoginInfo
   * @property {string} employeeLogoMobile - 左上角logo图片地址
   * @property {string} logoMobile - 中间标题logo图片地址
   * @property {string} title - 中间标题文本
   */

  /**
   * 获取登录页信息
   * @returns {Promise<LoginInfo>}
   */
  getLoginInfo() {
    return request.get(`${path}/logon-title`, {
      params: {
        languageStr: i18n.locale,
        x_app_id: util.getAppId(),
        t: Date.now(),
      },
    });
  },

  /**
   * 获取页首图标
   * @returns {Promise<AxiosResponse<Blob>>}
   */
  getLogonLogo() {
    return request.instance.get(`${path}/logon-logo`, { responseType: 'blob' });
  },

  /**
   * @typedef {Object} Lang
   * @property {string} name - 语言
   * @property {string} country - 国家
   */

  /**
   * 获取语言列表
   * @returns {Promise<Array<Lang>>}
   */
  getLangList() {
    return request.get(`${path}/languages`);
  },

  /**
   * 修改语言Cookie
   * @param {string} lang - 语言代码
   * @returns {Promise<*>}
   */
  changeLang(lang) {
    return request.post(`${path}/change-lang`, { lang });
  },

  /**
   * @typedef {Object} CountryCode
   * @property {string} areaCode - 国际电话区号
   * @property {string} name - 地区名称
   * @property {string} twoCode - ISO 3166-1二位字母代码
   */

  /**
   * 获取国家代码列表
   * @returns {Promise<Object.<string,Array<CountryCode>>>}
   */
  getCountryCodeList() {
    return request.get(`${path}/country-code-list`, { params: { lang: i18n.locale } });
  },

  /**
   * @typedef {Object} OtherSettings
   * @property {number} showHelpGuide - 是否显示遇到问题
   * @property {number} showPrivacyStatement -是否显示隐私政策
   */

  /**
   * @typedef {Object} AppPolicy
   * @property {string} registrationType - 注册方式（手机/邮箱）
   * @property {Array<string>} loginType - 登录方式（账密/短信/二维码/一键登录）
   * @property {OtherSettings} otherSettings - 其他配置
   * @property {number} enableUpgradeHwId - 判断是否直接跳转hwid-login
   * @property {number} enablehwidLogin - 判断是否单独显示华为ID登录按钮
   * @property {string} hwidThirdMibilePolicy - 区分华为ID登录跳转地址
   * @property {string} hwLoginAddress - 华为ID登录跳转地址1
   * @property {string} hwLoginAddressMobile - 华为id登录跳转地址2
   * @property {number} status
   * @property {boolean} thirdPartyAllow - status和thirdpartyType共同控制其他三方登录
   * @property {number} thirdpartyType - 是否直接跳转
   * @property {string} hideSection - 配置显示的三方登录
   * @property {boolean} whiteTenantID - 是否配置租户白名单，只有白名单(支持多租)才走云下环境
   */

  /**
   * 获取应用策略
   * @returns {Promise<AppPolicy>}
   */
  getAppPolicy() {
    return request.get(`${path}/app-policy`, {
      params: {
        time: Date.now(),
        appUrl: encodeURIComponent(util.getURLSearchParam('redirect')),
        x_app_id: util.getAppId(),
        client: 'mobile',
      },
    });
  },

  /**
   * @typedef {Object} AuthSource
   * @property {string} sourceNameCn - 中文名
   * @property {string} sourceNameEn - 英文名
   * @property {string} logo - 图标
   * @property {number} sourceType
   * @property {string} sourceCode
   */

  /**
   * @typedef {Object} AuthSourceConfig
   * @property {Array<AuthSource>} enterprise - 企业认证源
   * @property {Array<AuthSource>} medium - 社交媒体认证源
   */

  /**
   * 获取认证源配置
   * @param {Array<string>} sourceCodes
   * @returns {Promise<AuthSourceConfig>}
   */
  getAuthSourceConfig(sourceCodes) {
    return request.post(`${path}/auth-source-config`, { sourceCodes });
  },

  /**
   * 获取验证码类型
   * @returns {Promise<AxiosResponse<any>>}
   */
  getCodeSwitch() {
    return request.instance.get(`${path}/get-code-switch`, {
      params: { x_app_id: util.getAppId(), t: Date.now() },
    });
  },

  /**
   * @typedef {Object} CaptchaParams
   * @property {string} captchaJs
   * @property {string} appId
   * @property {string} businessId
   * @property {string} sceneId
   * @property {string} serviceDomain
   */

  /**
   * 获取验证码组件配置
   * @returns {Promise<CaptchaParams>}
   */
  getCaptchaParams() {
    return request.get(`${path}/captcha-params`);
  },

  /**
   * 获取图形验证码
   * @returns {Promise<AxiosResponse<Blob>>}
   */
  getUidCode() {
    return request.instance.get(`${path}/uid-code`, {
      params: { timeStamp: Date.now() },
      responseType: 'blob',
    });
  },

  /**
   * @typedef {Object} FirstVerifyRes
   * @property {number} captchaType
   * @property {string} challenge
   * @property {string} hcg
   * @property {number} hct
   * @property {number} resultCode
   */

  /**
   * 第一次校验，获取滑块验证码的必要参数
   * @param {CaptchaParams} params
   * @returns {Promise<FirstVerifyRes>}
   */
  firstVerify({ appId, businessId, sceneId, callCountryCode }) {
    return request.post(`${path}/first-verify`, { appId, businessId, sceneId, callCountryCode });
  },

  /**
   * 滑块验证码验证成功后的第二次校验
   */
  secondVerify(commonOptions, { challenge, hcg, hct, validate }, { phoneNumber }) {
    return request.post(`${path}/second-verify`, {
      ...commonOptions,
      challenge,
      hcg,
      hct,
      validate,
      loginAccount: phoneNumber,
    });
  },

  /**
   * @typedef {Object} AgreementRes
   * @property {string} siteId
   * @property {number|string} tenantId
   * @property {string} name
   */

  /**
   * 获取隐私声明配置
   * @returns {Promise<AgreementRes>}
   */
  getPrivacyStatement() {
    return request.get(`${path}/get-privacy-statement`, {
      params: { language: i18n.locale, dt: Date.now() },
    });
  },

  /**
   * 获取用户协议配置
   * @returns {Promise<AgreementRes>}
   */
  getUserAgreement() {
    return request.get(`${path}/get-agreement`, {
      params: { language: i18n.locale, dt: Date.now() },
    });
  },

  /**
   * 发送验证码
   * @returns {Promise<*>}
   */
  sendSMSCode({ countryCode, phoneNumber, pageToken }, params) {
    const { ticket, smsFigureCode, timeStamp } = params;
    const loginParams = {
      appId: util.getAppId(),
      fingerPrint: util.fingerprint(),
      language: i18n.locale,
      phoneNumber: phoneNumber,
      ticket: ticket || '',
      callCountryCode: countryCode.split(' ')[0],
      pageToken,
    };
    // 图形验证码
    if (smsFigureCode && timeStamp) {
      loginParams.smsFigureCode = smsFigureCode;
      loginParams.timeStamp = timeStamp;
    }
    return request.post(`${path}/login/send-code`, loginParams);
  },

  /**
   * 登录/注册
   * @returns {Promise<*>}
   */
  loginOrRegister({ countryCode, phoneNumber, verificationCode }, params) {
    const { ticket, safeKey, uid, agreement, appUrl, twoCode, imgCode } = params;
    const resultParams = {
      ...util.getSDKParams(),
      countryCode: twoCode,
      callCountryCode: countryCode.split(' ')[0],
      phoneNumber,
      phoneVerifyCode: verificationCode,
      registerType: 'phone',
      appUrl,
      language: i18n.locale,
      isAgreementSigned: true,
      agreementLanguage: i18n.locale,
      agreement: agreement ? AGREEMENT.yes : AGREEMENT.no,
      appId: util.getAppId(),
      fingerPrint: util.fingerprint(),
      ticket: ticket || '',
      wap: '1',
    };
    if (uid) {
      resultParams.uid = uid;
      resultParams.accountType = 'sms';
    }

    return request.post(`${path}/login/code`, resultParams);
  },

  crossBorderAuth({ crossBorderKey }) {
    return request.post(`${path}/cross-border-auth`, {
      ...util.getSDKParams(),
      crossBorderKey: crossBorderKey || '',
    });
  },

  getMobileResource(mtokenKey) {
    return request.get(`${path}/get-mobile-resource`, { params: { dt: Date.now(), mtokenKey } });
  },

  updateSignAgreement({ uid, uniid, uuid, needSign, needSignKey }) {
    return request.post(`${path}/update-sign-agreement`, {
      agreementLanguage: i18n.locale,
      clientIp: '',
      idAccountVo: {
        uid: uid || '',
        uniid: uniid || '',
        uuid: uuid || '',
        tenantId: '',
      },
      status: needSign,
      needSignKey,
    });
  },

  addScript(url, config) {
    return request.get(url, config);
  },

  /**
   * @typedef CookieStatement
   * @property {boolean} visible
   */

  /**
   * 获取Cookie配置
   * @return {Promise<CookieStatement>}
   */
  getCookieStatement() {
    return request.get(`${path}/get-cookie-statement`, {
      params: { language: i18n.locale },
    });
  },
};
